import { useCallback } from 'react';
import { usePlatform } from '../platform/context';
import { applyImport, createLinkItem, EVERYTHING } from '../db/imports';
import type { Container } from '../db/board';
import { useImportStore } from './importStore';
import { FetchBlockedError, runImport } from './run';

/**
 * Starting an import and letting it run.
 *
 * The item is created first and the pipeline fills it in afterwards, which is
 * what lets the panel open immediately on something real. A failure leaves the
 * item standing with its URL — the stage doc's rule that the user always ends
 * up with an item.
 */
export function useImport() {
  const platform = usePlatform();

  const start = useImportStore((state) => state.start);
  const advance = useImportStore((state) => state.advance);
  const fail = useImportStore((state) => state.fail);
  const finish = useImportStore((state) => state.finish);

  /** Runs against an item that already exists. Also the paste-instead retry. */
  const importInto = useCallback(
    async (itemId: string, url: string, html?: string) => {
      start(itemId, url);

      try {
        const result = await runImport({
          platform,
          url,
          html,
          onProgress: (progress) => advance(itemId, progress),
        });

        await applyImport(itemId, result, EVERYTHING);
        finish(itemId, result.failedImages);
      } catch (thrown) {
        // A blocked fetch is the one failure with a way forward, so it's
        // flagged separately: the panel offers to paste the page instead.
        fail(itemId, messageFor(thrown), thrown instanceof FetchBlockedError);
      }
    },
    [platform, start, advance, fail, finish],
  );

  /** The add-item flow. Returns the id to open the panel on. */
  const beginNew = useCallback(
    async (container: Container, url: string) => {
      const itemId = await createLinkItem(container, url);
      // Deliberately not awaited: the caller navigates to the panel now and
      // the pipeline reports into the store from there.
      void importInto(itemId, url);
      return itemId;
    },
    [importInto],
  );

  return { beginNew, importInto };
}

export function messageFor(thrown: unknown): string {
  // Rust hands back sentences written to be read; so does FetchBlockedError.
  if (thrown instanceof Error && thrown.message !== '') return thrown.message;
  return "That link couldn't be imported.";
}
